import {gql, useQuery} from '@apollo/client';

/**
 * The currencies a reward or partner rate may be denominated in.
 *
 * Fetched rather than hardcoded. Currency now decides whether money is earned
 * at all — a tier or commission whose currency does not match a tracker earns
 * nothing, silently — so a stale local list is a way to lose money quietly.
 * `getPlatformSupportedCurrenciesForAdmin` is the same source the wallet admin
 * uses, and it reports `enabled`, so a currency switched off centrally stops
 * being offered here without a release.
 */
export const GET_PLATFORM_CURRENCIES = gql`
  query GetPlatformSupportedCurrenciesForAdmin {
    getPlatformSupportedCurrenciesForAdmin {
      ... on Error {
        message
        statusCode
      }
      ... on PlatformSupportedCurrencyList {
        currencies {
          enabled
          currency
          currencySymbol
          currencyName
        }
      }
    }
  }
`;

export const usePlatformCurrencies = () => {
  const {data, loading} = useQuery(GET_PLATFORM_CURRENCIES, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-first',
  });

  const all = data?.getPlatformSupportedCurrenciesForAdmin?.currencies ?? [];

  const currencies = all
    .filter(entry => entry?.enabled && entry?.currency)
    .map(entry => ({
      value: entry.currency,
      symbol: entry.currencySymbol,
      label: entry.currencyName
        ? `${entry.currency} — ${entry.currencyName}`
        : entry.currency,
    }));

  return {currencies, loading};
};

/**
 * Formats an amount with the currency it is actually in.
 *
 * The partner screens hardcoded `XOF` beside figures the gateway had already
 * converted — to USD on the list and metrics endpoints — so the label
 * contradicted the number. Passing the currency through means the label can
 * only ever be the one the server reported, and an unknown currency renders as
 * a dash rather than guessing.
 */
export const formatMoney = (amount, currency, symbols = {}) => {
  if (amount === null || amount === undefined) return '—';
  const value = Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (!currency) return value;
  const symbol = symbols?.[currency]?.symbol;
  return symbol ? `${symbol} ${value}` : `${currency} ${value}`;
};

export default usePlatformCurrencies;
